/// AI controller — flies an aircraft to its goal with no player input, by
/// issuing the SAME commands/clearances a player would; the existing autopilots
/// (tickDirectTo / tickApproachIntercept / tickLanding / tickFixExit) do the
/// steering. Port of the iOS `AIController` (Support/AIController.swift):
///   - land/arrival: glide-slope descent → localizer intercept → landing clearance
///   - transition:   steer to the exit fix at cruise (tickFixExit hands it off)
///   - takeoff:      clear for takeoff → climb out → exit waypoint
///   - landHelipad:  steer to the pad; the auto-engage owns the touchdown
///
/// Stepped ~once/second (GameSession.tickAI); the per-frame autopilot ticks run
/// every frame as usual and execute the targets this sets.

import type { Aircraft } from './Aircraft'
import type { Airport, LevelData } from '../types/LevelData'
import type { CoordinateProjector } from '../geo/CoordinateProjector'
import { setHeading, setAltitude, setSpeed } from './CommandService'
import {
  evaluateLandingClearance,
  issueLandingClearance,
  evaluateApproachClearance,
  issueApproachClearance,
  commandTakeoff,
} from './Autopilot'
import { pickActiveRunwayEnd } from './spawner'
import { performanceFor } from './AircraftDatabase'

export interface AIContext {
  level: LevelData
  airports: Airport[]
  projector: CoordinateProjector
  pxPerNM: number
  windDirection: number
}

const GLIDESLOPE_FT_PER_NM = 318 // standard ~3° glide slope
const MIN_APPROACH_ALT_FT = 1000 // floor above field elevation on approach

/// Magnetic heading (0–359) from one screen point to another (Y-down).
function headingTo(from: { x: number; y: number }, to: { x: number; y: number }): number {
  const dx = to.x - from.x
  const dy = to.y - from.y
  return ((Math.atan2(dx, -dy) * 180) / Math.PI + 360) % 360
}
function angDiff(a: number, b: number): number {
  return Math.abs((((a - b + 180) % 360) + 360) % 360 - 180)
}
function glideAlt(elevFt: number, distNM: number): number {
  return Math.max(elevFt + MIN_APPROACH_ALT_FT, elevFt + distNM * GLIDESLOPE_FT_PER_NM)
}

/// Decide + issue one tick of control for an AI aircraft.
export function updateAIAircraft(aircraft: Aircraft, ctx: AIContext): void {
  if (!aircraft.aiControlled) return
  // Engine-owned phases — nothing for the AI to decide.
  if (
    aircraft.status === 'landing' ||
    aircraft.status === 'landed' ||
    aircraft.status === 'completed' ||
    aircraft.status === 'takingOff'
  )
    return
  if (aircraft.collisionStatus === 'crashed') return

  const goal = aircraft.goal
  if (goal.land) return aiLand(aircraft, goal.land.airportName, ctx)
  if (goal.arrival) {
    // While the STAR flight plan runs, the engine flies it; take over to land
    // only once it's complete (flightPlan cleared).
    if (!aircraft.flightPlan) aiLand(aircraft, goal.arrival.airportName, ctx)
    return
  }
  if (goal.transition) return aiTransition(aircraft, goal.transition.waypointName, ctx)
  if (goal.takeoff) return aiTakeoff(aircraft, goal.takeoff.airportName, goal.takeoff.waypointName, ctx)
  if (goal.landHelipad)
    return aiHelipad(aircraft, goal.landHelipad.airportName, goal.landHelipad.helipadId, ctx)
}

function aiLand(aircraft: Aircraft, airportName: string, ctx: AIContext): void {
  const airport = ctx.airports.find((a) => a.name === airportName)
  if (!airport || airport.runways.length === 0) return

  // Pick + lock the active runway once (prevents oscillation as wind drifts).
  if (!aircraft.aiRunway) {
    const pick = pickActiveRunwayEnd(airport, ctx.windDirection)
    if (!pick) return
    aircraft.aiRunway = { runwayId1: pick.runway.id1, approachHeadingDeg: pick.headingDeg }
  }
  const runway = airport.runways.find((r) => r.id1 === aircraft.aiRunway?.runwayId1)
  if (!runway) {
    aircraft.aiRunway = null
    return
  }
  const approachHeadingDeg = aircraft.aiRunway.approachHeadingDeg
  const args = { aircraft, airport, runway, approachHeadingDeg, projector: ctx.projector, pxPerNM: ctx.pxPerNM }

  const ev = evaluateLandingClearance(args)
  const elev = airport.elevation ?? 0

  // Continuous glide-slope descent + approach speed.
  const target = glideAlt(elev, ev.distanceNM)
  if (Math.abs(aircraft.altitudeFt - target) > 400) setAltitude(aircraft, target)
  const perf = performanceFor(aircraft.type)
  const apprSpeed = Math.round(perf.normalLandingSpeedKts * 1.3)
  if (aircraft.targetSpeedKts > apprSpeed) setSpeed(aircraft, apprSpeed)

  // Final gate: cleared to land → the engine flies the touchdown.
  if (ev.ok) {
    issueLandingClearance(args)
    return
  }
  // Not on the localizer yet: engage the intercept autopilot once in range,
  // otherwise fly toward the threshold to get into range. Once intercepting,
  // leave the steering to tickApproachIntercept (don't re-set directTo).
  if (!aircraft.approachIntercept) {
    const ae = evaluateApproachClearance(args)
    if (ae.ok) {
      issueApproachClearance(args)
      return
    }
    aircraft.directToPoint = ev.thresholdPx
    aircraft.directToName = `RWY ${approachHeadingDeg === runway.heading1 ? runway.id1 : runway.id2}`
  }
}

function aiTransition(aircraft: Aircraft, waypointName: string, ctx: AIContext): void {
  const wp = ctx.level.waypoints.find((w) => w.name === waypointName)
  if (wp) {
    // Transition goals aren't steered by tickDirectTo — aim with a heading.
    const target = ctx.projector.project(wp.coordinate, true)
    const hdg = headingTo(aircraft.position, target)
    if (angDiff(aircraft.targetHeadingDeg, hdg) > 3) setHeading(aircraft, hdg)
  }
  // Climb to / hold a cruise altitude until it crosses the fix (tickFixExit).
  const perf = performanceFor(aircraft.type)
  const cruise = Math.round((perf.minAltitudeFt + perf.maxAltitudeFt) / 2)
  if (Math.abs(aircraft.altitudeFt - cruise) > 500) setAltitude(aircraft, cruise)
}

function aiTakeoff(aircraft: Aircraft, airportName: string, waypointName: string, ctx: AIContext): void {
  if (aircraft.status === 'awaitingTakeoff') {
    commandTakeoff(aircraft, ctx.level, ctx.projector)
    return
  }
  if (aircraft.status !== 'enRoute') return
  const wp = ctx.level.waypoints.find((w) => w.name === waypointName)
  if (wp) {
    const target = ctx.projector.project(wp.coordinate, true)
    const hdg = headingTo(aircraft.position, target)
    if (angDiff(aircraft.targetHeadingDeg, hdg) > 3) setHeading(aircraft, hdg)
  }
  const elev = ctx.airports.find((a) => a.name === airportName)?.elevation ?? 0
  const perf = performanceFor(aircraft.type)
  if (aircraft.altitudeFt < elev + 5000 && aircraft.targetAltitudeFt < elev + 5000) {
    setAltitude(aircraft, Math.min(elev + 10000, perf.maxAltitudeFt))
  }
}

function aiHelipad(aircraft: Aircraft, airportName: string, helipadId: string, ctx: AIContext): void {
  if (aircraft.status !== 'enRoute') return // auto-engage owns the touchdown
  const airport = ctx.airports.find((a) => a.name === airportName)
  const pad = airport?.helipads?.find((p) => p.id === helipadId)
  if (!pad) return
  const target = ctx.projector.project(pad.coordinate, true)
  const hdg = headingTo(aircraft.position, target)
  if (angDiff(aircraft.targetHeadingDeg, hdg) > 3) setHeading(aircraft, hdg)
  if (Math.abs(aircraft.altitudeFt - 3000) > 500) setAltitude(aircraft, 3000)
}
